<template>
  <v-container fluid class="pa-4">
    <v-card>
      <v-card-title class="d-flex justify-space-between">
        <span class="title">{{ $t('title.Maintenance') }}</span>
        <div class="d-flex">
          <v-tooltip location="bottom" content-class="custom-tooltip">
            <template #activator="{ props }">
              <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="action-btn mr-2"
                icon="mdi-eye" @click="printMaintenances" />
            </template>
            {{ $t('general.viewprint') }}
          </v-tooltip>

          <v-tooltip location="bottom" content-class="custom-tooltip">
            <template #activator="{ props }">
              <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="action-btn" icon="mdi-plus"
                @click="addMaintenance" />
            </template>
            {{ $t('general.addMaintenance') }}
          </v-tooltip>
        </div>
      </v-card-title>

      <v-card-text>
        <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" class="mb-2" density="compact"
          autocomplete="off" />

        <v-data-table :headers="headers" :items="filteredMaintenances" :loading="loading"
          :loading-text="$t('general.loading')" class="min-width-table custom-header" :items-per-page="15">

          <!-- Bold naslovi i sort ikonice -->
          <template v-slot:headers="{ columns, toggleSort, isSorted, getSortIcon }">
            <tr>
              <th v-for="column in columns" :key="column.value" @click="() => column.sortable && toggleSort(column)"
                :style="{
                  cursor: column.sortable ? 'pointer' : 'default',
                  width: column.width,
                  maxWidth: column.width,
                  textAlign:
                    column.align === 'center'
                      ? 'center'
                      : column.align === 'end'
                        ? 'right'
                        : column.value === 'actions'
                          ? 'right'
                          : 'left'
                }" class="sortable-header">
                <div class="d-flex align-center w-100" :class="column.value === 'actions'
                  ? 'justify-end'
                  : ['document', 'statusId'].includes(column.value)
                    ? 'justify-center'
                    : 'justify-space-between'
                  ">
                  <strong>{{ column.text }}</strong>

                  <v-icon v-if="column.sortable" size="12" class="sort-icon ml-2" :class="{ active: isSorted(column) }">
                    {{ isSorted(column) ? getSortIcon(column) : 'mdi-arrow-up-down' }}
                  </v-icon>
                </div>
              </th>
            </tr>
          </template>

          <!-- Redovi -->
          <template v-slot:item="{ item }">
            <tr :class="rowClass(item)">
              <td>
                {{ item.raw?.Name ?? item.Name }}
              </td>
              <td>{{ formatDate(item.raw?.Date ?? item.Date) }}</td>
              <td style="display: flex; align-items: center; justify-content: space-between;">
                {{ formatDate(item.raw?.Expired ?? item.Expired) }}
                <v-icon v-if="isExpiredContract(item.raw?.Expired ?? item.Expired)" color="red" size="16" class="ml-1">
                  mdi-clock-alert
                </v-icon>
              </td>
              <td style="text-align: right;">{{ formatMoney(item.raw?.Value ?? item.Value) }}</td>

              <td class="text-end">
                <v-chip size="small" class="remaining-chip" style="font-size:12px; font-weight:normal;"
                  :color="(item.raw?.Preostalo ?? item.Preostalo) <= 0 ? 'red' : 'green'" variant="flat">
                  <span class="chip-number">
                    {{ formatMoney(item.raw?.Preostalo ?? item.Preostalo) }}
                  </span>
                </v-chip>
              </td>

              <td class="text-center">
                <div style="display: flex; align-items: center; justify-content: space-between;">

                  <v-tooltip location="bottom" content-class="custom-tooltip">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" size="small" variant="outlined" color="teal" class="action-btn"
                        :disabled="!(item.raw?.FileName ?? item.FileName)"
                        @click="previewFile(item.raw?.FileName ?? item.FileName)" style="margin-right: 5px;">
                        <v-icon size="16">mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    {{ $t('general.pregledDokumenta') }}
                  </v-tooltip>

                  <v-tooltip location="bottom" content-class="custom-tooltip">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" size="x-small" variant="outlined" color="primary"
                        class="mr-2 action-btn-small" @click="changeDoc(item.raw ?? item)">
                        <v-icon size="14" color="primary">mdi-pencil</v-icon>
                      </v-btn>
                    </template>

                    {{ $t('general.editDokument') }}
                  </v-tooltip>
                  <v-tooltip location="bottom" content-class="custom-tooltip">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" size="x-small" variant="text" color="red" class="mr-2 action-btn-small"
                        @click="openDeleteDoc(item.raw ?? item)" :disabled="!(item.raw?.FileName ?? item.FileName)">
                        <v-icon size="14">mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    {{ $t('general.delete') }}
                  </v-tooltip>
                </div>
              </td>
              <td>
                <v-menu location="bottom" offset="4">
                  <template #activator="{ props }">
                    <v-btn v-bind="props" variant="text" class="status-btn">
                      <img v-if="parseInt(item.StatusId) === 1" :src="statusImages.aktivan" height="22" />
                      <img v-if="parseInt(item.StatusId) === 2" :src="statusImages.neaktivan" height="22" />
                    </v-btn>
                  </template>

                  <v-list density="compact">
                    <v-list-item v-for="s in statusOptions" :key="s.id" :disabled="s.id === item.StatusId"
                      @click="s.id !== item.StatusId && askConfirmStatusChange(item, s.id)">
                      <v-list-item-title class="d-flex align-center justify-space-between">
                        {{ s.name }}

                        <v-icon v-if="s.id === item.StatusId" size="14">
                          mdi-check
                        </v-icon>
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>

              <!-- IKONICE-->
              <td class="actions-cell">
                <div class="d-flex align-center justify-end action-buttons">

                  <!-- ULAGANJE -->
                  <v-tooltip location="bottom" content-class="custom-tooltip">
                    <template #activator="{ props }">
                      <router-link :to="{ name: 'maintenance-detail', params: { id: (item.raw?.Id ?? item.Id) } }"
                        class="inv-link">
                        <v-btn v-bind="props" size="small" variant="outlined" color="info" class="action-btn mr-2">
                          <v-icon size="18">mdi-cash-plus</v-icon>
                        </v-btn>
                      </router-link>
                    </template>

                    {{ $t('general.trosak') }}
                  </v-tooltip>

                  <!-- EDIT -->
                  <v-tooltip location="bottom" content-class="custom-tooltip">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="mr-2 action-btn"
                        :disabled="(item.raw?.statusId ?? item.statusId) === 3"
                        @click="editMaintenance(item.raw ?? item)">
                        <v-icon size="16">mdi-pencil</v-icon>
                      </v-btn>
                    </template>

                    {{ $t('general.uredi') }}
                  </v-tooltip>

                  <!-- DELETE -->
                  <v-tooltip location="bottom" content-class="custom-tooltip">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" size="small" variant="outlined" color="red" class="action-btn"
                        @click="openDeleteDialog(item.raw ?? item)">
                        <v-icon size="16">mdi-delete</v-icon>
                      </v-btn>
                    </template>

                    {{ $t('general.delete') }}
                  </v-tooltip>

                </div>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dijalog: Dodaj / Uredi održavanje -->
    <BaseModal v-model="dialog" :title="editMode
      ? this.$t('modalni.maintenance.uredi')
      : this.$t('modalni.maintenance.dodaj')">
      <v-form ref="maintenanceForm" v-model="isFormValid">
        <v-text-field v-model="form.contract_name" :label="$t('table.maintenance.naziv')" :rules="[rules.required]"
          required class="my-text" />

        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field v-model="form.date_signing_contract" :label="$t('table.maintenance.date_signing_contract')"
              type="date" :rules="[rules.required]" clearable clear-icon="mdi-close" class="my-text" required />
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field v-model="form.date_expire_contract" :label="$t('table.maintenance.date_expire_contract')"
              type="date" :rules="[rules.required]" clearable clear-icon="mdi-close" class="my-text" required />
          </v-col>
        </v-row>

        <template v-if="editMode">
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select v-model="form.statusId" :items="statusOptions" item-title="name" item-value="id"
                :label="$t('modalni.maintenance.statusUgovora')" :rules="[rules.required]" class="my-selectModal"
                required />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field v-model="form.contract_value" :label="$t('modalni.maintenance.vrijednost')" v-money
                :rules="[rules.required]" class="my-text" required />
            </v-col>
          </v-row>
        </template>

        <v-textarea v-model="form.notes" :label="$t('modalni.maintenance.napomena')" rows="3" auto-grow
          class="my-text" />

        <template v-if="!editMode">
          <v-row dense>
            <v-col cols="12">
              <v-text-field v-model="form.contract_value" :label="$t('modalni.maintenance.vrijednost')" v-money
                :rules="[rules.required, rules.notZero]" class="my-text" required /> </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <v-file-input v-model="form.document" :label="$t('modalni.maintenance.dokument')"
                prepend-icon="mdi-paperclip" accept="application/pdf,image/*" class="my-text" />
            </v-col>
          </v-row>
        </template>

      </v-form>

      <template #footer>
        <div class="d-flex justify-end ga-2">

          <!-- OTKAŽI -->
          <v-btn variant="outlined" @click="closeDialog">
            <span style="font-size:14px; font-weight: 600;">{{ $t('general.cancel') }}</span>
          </v-btn>

          <!-- SAČUVAJ -->
          <v-btn color="primary" variant="flat" @click="saveMaintenance">
            <span style="font-size:14px; font-weight: 600;">{{ $t('general.save') }}</span>
          </v-btn>
        </div>
      </template>


    </BaseModal>

    <!-- PREGLED DOKUMENTA -->
    <BaseModal v-model="documentPreview" size="xl" :title="$t('general.pregledDokumenta')" height="90vh"
      modalClass="report-modal" :hideFooter="true">

      <template #header-actions>
        <v-tooltip location="bottom" content-class="custom-tooltip" :text="$t('general.download')">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon variant="plain" @click="downloadCurrentFile">
              <v-icon size="18" style="padding-left: 10px;">mdi-download</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

        <v-tooltip location="bottom" content-class="custom-tooltip" :text="$t('general.print')">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon variant="plain" @click="printCurrentFile">
              <v-icon size="18">mdi-printer</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </template>

      <iframe ref="documentFrame" style="width:100%; height:100%; border:none;" />

    </BaseModal>

    <!-- Dijalog za potvrdu brisanja sredstva -->
    <BaseModal v-model="deleteDialog" :title="this.$t('modalni.maintenance.brisi')">
      <p>{{ this.$t('modalni.maintenance.brisanjePoruka') }} <strong>{{ deleteMaintenanceName }}</strong>?</p>

      <template #footer>
        <div class="d-flex justify-end ga-2">
          <!-- OTKAŽI -->
          <v-btn variant="outlined" @click="deleteDialog = false">
            <span style="font-size:14px; font-weight: 600;">{{ this.$t('general.cancel') }}</span>
          </v-btn>

          <!-- OBRIŠI -->
          <v-btn color="danger" variant="flat" @click="confirmDeleteMaintenance">
            <span style="font-size:14px; font-weight: 600; color: #FFF">{{ this.$t('general.deleteButton') }}</span>
          </v-btn>
        </div>
      </template>
    </BaseModal>

    <!-- PROMJENA STATUSA -->
    <BaseModal v-model="confirmStatusDialog" :title="this.$t('modalni.maintenance.promjenaStatusaModalni')">
      <p class="confirm-text" style="font-size: 14px;">
        {{ this.$t('modalni.maintenance.promjenaStatusaPoruka1') }}
        <strong>{{ pendingStatusChange.maintenance?.contract_name }}</strong>
        {{ this.$t('modalni.maintenance.promjenaStatusaPoruka2') }}
        <strong>{{ getStatusName(pendingStatusChange.newStatusId) }}</strong>
      </p>

      <template #footer>
        <div class="d-flex justify-end ga-2">
          <v-btn variant="outlined" @click="cancelStatusChange">
            <span style="font-size:14px; font-weight: 600;">{{ this.$t('general.cancel') }}</span>
          </v-btn>

          <v-btn color="primary" @click="confirmStatusChange">
            <span style="font-size:14px; font-weight: 600;">{{ this.$t('general.potvrdi') }}</span>
          </v-btn>
        </div>
      </template>
    </BaseModal>

    <!--Save/Print dialog-->
    <BaseModal v-model="reportPreview" size="xl" :title="$t('print.maintenance')" height="90vh"
      modalClass="report-modal" :hideFooter="true">

      <template #header-actions>

        <v-tooltip location="bottom" content-class="custom-tooltip" :text="$t('general.download')">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon variant="plain" @click="downloadPDF">
              <v-icon size="18"
                style="padding-top: 2px !important; padding-left: 10px !important;">mdi-download</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

        <v-tooltip location="bottom" content-class="custom-tooltip" :text="$t('general.print')">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon variant="plain" @click="printReport">
              <v-icon size="18">mdi-printer</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

      </template>

      <iframe ref="reportFrame" :style="{
        width: '100%',
        height: '100%',
        border: 'none',
        transform: `scale(${zoom})`,
        transformOrigin: 'top left'
      }" />

    </BaseModal>

    <!-- PROMJENA DOKUMENTA -->
    <BaseModal v-model="changeDocumentDialog" :title="this.$t('modalni.maintenance.changeDoc')">

      <v-file-input v-model="form.document" :label="$t('modalni.maintenance.dokument')" prepend-icon="mdi-paperclip"
        accept="application/pdf,image/*" class="my-text" />

      <template #footer>
        <div class="d-flex justify-end ga-2">
          <v-btn variant="outlined" @click="cancelChangeDocument">
            <span style="font-size:14px; font-weight: 600;">{{ this.$t('general.cancel') }}</span>
          </v-btn>

          <v-btn color="primary" @click="confirmChangeDocument">
            <span style="font-size:14px; font-weight: 600;">{{ this.$t('general.potvrdi') }}</span>
          </v-btn>
        </div>
      </template>
    </BaseModal>

    <!-- BRISANJE DOKUMENTA -->
    <BaseModal v-model="deleteDocDialog" :title="$t('general.deleteDoc')">
      <p>{{ $t('modalni.popis.potvrdaBrisanjaDokumenta') }} <strong>{{ tempUgovor?.Name }}</strong>?</p>
      <template #footer>
        <div class="d-flex justify-end ga-2">
          <v-btn variant="outlined" @click="deleteDocDialog = false">
            <span style="font-size:14px; font-weight:600;">{{ $t('general.cancel') }}</span>
          </v-btn>
          <v-btn color="danger" variant="flat" @click="confirmDeleteDoc">
            <span style="font-size:14px; font-weight:600; color:#FFF">{{ $t('general.deleteButton')
              }}</span>
          </v-btn>
        </div>
      </template>
    </BaseModal>

  </v-container>
</template>

<script>
import BaseModal from '@/components/dialogs/BaseModal.vue'
import { printTable, fetchHeaderImage } from "@/services/printService";
import html2pdf from "html2pdf.js"
import { format } from 'date-fns';

export default {
  name: 'MaintenancePage',
  components: {
    BaseModal
  },
  data() {
    return {
      documentPreview: false,
      currentFileName: null,
      currentFileUrl: null,
      html: '',
      zoom: 1,
      reportPreview: false,
      confirmStatusDialog: false,
      pendingStatusChange: {
        maintenance: null,
        newStatusId: null,
        oldStatusId: null
      },
      isFormValid: false,
      deleteDialog: false,
      deleteMaintenanceId: null,
      deleteMaintenanceName: "",
      changeDocumentDialog: false,

      search: '',
      loading: false,
      dialog: false,
      editMode: false,
      selectedId: null,
      maintenances: [],
      devices: [],
      softwares: [],
      form: {
        contract_name: '',
        date_signing_contract: '',
        date_expire_contract: '',
        notes: '',
        device: null,
        software: null,
        document: null,
        file_name: '',
        contract_value: '',
        statusId: 1
      },
      oldDocUrl: null,
      headers: [
        { text: this.$t('table.maintenance.naziv'), value: 'Name', sortable: true, width: 'calc(42% - 160px)' },
        { text: this.$t('table.maintenance.date_signing_contract'), value: 'Date', sortable: true, width: "11%" },
        { text: this.$t('table.maintenance.date_expire_contract'), value: 'Expired', sortable: true, width: "11%" },
        { text: this.$t('table.maintenance.contract_value'), value: 'Value', sortable: true, width: "9%" },
        { text: this.$t('table.maintenance.remaining'), value: 'Preostalo', align: 'end', sortable: true, width: "9%" },
        { text: this.$t('table.maintenance.document'), value: 'document', align: 'center', sortable: false, width: "100px" },
        { text: this.$t('table.maintenance.status'), align: 'center', value: 'StatusId', width: "130px" },
        { text: this.$t('general.optionsUPPER'), value: 'actions', sortable: false, width: "160px" },
      ],
      tempId: '',
      tempUgovor: ["Id", "Name"],
      deleteDocDialog: false
    };
  },
  computed: {
    rules() {
      return {
        required: v => !!v || this.$t('validation.required'),
        notZero: v => {
          const num = Number(String(v).replace(/\./g, '').replace(',', '.'))
          return (!!v && !isNaN(num) && num > 0) || this.$t('validation.notZero')
        }
      }
    },
    imageLang() {
      const lang = localStorage.getItem('lang')
      if (lang === 'sr' || lang === 'cir-ekavica') return 'Cir'
      if (lang === 'en') return 'Eng'
      return 'Lat'
    },
    statusImages() {
      const lang = this.imageLang
      return {
        aktivan: `/img/aktivan${lang}.png`,
        neaktivan: `/img/neaktivan${lang}.png`,
        rashodovan: `/img/rashodovan${lang}.png`,
        predlogRashoda: `/img/predlogRashoda${lang}.png`,
      }
    },
    statusOptions() {
      return [
        { id: 1, name: this.$t('general.aktivan') },
        { id: 2, name: this.$t('general.neaktivan') },
      ]
    },
    filteredMaintenances() {
      const term = (this.search || '').toLowerCase();
      return this.maintenances.filter(m =>
        String(m.Name ?? '').toLowerCase().includes(term) ||
        String(m.Date ?? '').toLowerCase().includes(term) ||
        String(m.Expired ?? '').toLowerCase().includes(term) ||
        String(m.Value ?? '').toLowerCase().includes(term) ||
        String(m.Preostalo ?? '').toLowerCase().includes(term)
      )
    },
  },
  mounted() {
    this.fetchMaintenances();
  },
  methods: {
    isExpiredContract(dateStr) {
      if (!dateStr) return false
      const expired = new Date(dateStr)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return expired < today
    },
    rowClass(item) {
      const statusId = item.raw?.statusId ?? item.statusId
      if (statusId === 3) return 'row-isteklo'
      if (statusId === 2) return 'row-neaktivno'
      if (this.isExpiredContract(item.raw?.Expired ?? item.Expired)) return 'row-isteklo'
      if (statusId === 1) return 'row-aktivno'
      return ''
    },
    downloadPDF() {
      const container = document.createElement("div")
      container.innerHTML = this.html

      const title = container.querySelector("h2")?.innerText
      const fileName = this.generateReportFileName(title)

      html2pdf()
        .set({
          margin: 10,
          filename: fileName,
          html2canvas: { scale: 2 },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "landscape"
          }
        })
        .from(container)
        .save()
    },
    generateReportFileName(title) {

      const date = new Date()

      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()

      const compactDate = `${day}${month}${year}`

      const cleanTitle = title
        .replace(/[čć]/g, "c")
        .replace("ž", "z")
        .replace("š", "s")
        .replace("đ", "d")
        .split(" ")
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join("")

      return `${cleanTitle}_${compactDate}.pdf`
    },

    openReportPreview(html) {

      this.reportPreview = true

      this.$nextTick(() => {
        this.$nextTick(() => {

          const iframe = this.$refs.reportFrame

          if (!iframe) return

          const doc = iframe.contentWindow.document

          doc.open()
          doc.write(html)
          doc.close()

        })
      })
    },

    printReport() {
      const iframe = this.$refs.reportFrame
      iframe.contentWindow.print()
    },

    async previewFile(fileName) {
      console.log("test")
      this.currentFileName = fileName
      try {
        const response = await this.$axios.get('Upload?fileName=' + fileName, { responseType: 'arraybuffer' })
        const isImage = /\.(png|jpg|jpeg|webp|gif)$/i.test(fileName)
        const mimeType = isImage ? 'image/' + fileName.split('.').pop().toLowerCase() : 'application/pdf'
        const blob = new Blob([response.data], { type: mimeType })
        this.currentFileUrl = window.URL.createObjectURL(blob)
        this.documentPreview = true

        this.$nextTick(() => {
          this.$nextTick(() => {
            if (this.$refs.documentFrame) {
              this.$refs.documentFrame.src = this.currentFileUrl
            }
          })
        })
      } catch (err) {
        console.error(err)
      }
    },

    downloadCurrentFile() {
      if (!this.currentFileUrl) return
      const link = document.createElement('a')
      link.href = this.currentFileUrl
      link.setAttribute('download', this.currentFileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    printCurrentFile() {
      this.$refs.documentFrame?.contentWindow?.print()
    },

    parseMoney(value) {
      if (value === null || value === undefined || value === '') return null

      if (typeof value === 'number') return value

      return Number(
        value
          .replace(/\./g, '')
          .replace(',', '.')
      )
    },
    formatMoney(value) {
      if (value === null || value === undefined || value === '') return '-'

      const n = Number(value)
      if (Number.isNaN(n)) return '-'

      return (
        n.toLocaleString('de-DE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      )
    },
    async printMaintenances() {
      const columns = [
        { key: "name", label: this.$t("table.maintenance.naziv"), width: "31%" },
        { key: "dateSign", label: this.$t("table.maintenance.date_signing_contract"), width: "15%", align: "center" },
        { key: "dateExpire", label: this.$t("table.maintenance.date_expire_contract"), width: "15%", align: "center" },
        { key: "value", label: this.$t("table.maintenance.contract_value"), width: "15%", align: "right" },
        { key: "remaining", label: this.$t("table.maintenance.remaining"), width: "15%", align: "right" },
        { key: "status", label: this.$t("table.maintenance.status"), width: "9%", align: "center" },
      ];

      const data = this.filteredMaintenances.map((m) => ({
        name: m.Name ?? "",
        dateSign: this.formatDate(m.Date),
        dateExpire: this.formatDate(m.Expired),
        value: this.formatMoney(m.Value),
        remaining: this.formatMoney(m.Preostalo),
        status: this.getStatusName(m.StatusId),
      }));

      const headerImg = await fetchHeaderImage(this.$axios)
      const footer = `
    ${this.$t("exportToExcel.pokrenuo")} ${localStorage.getItem("Name")} <br />
    ${this.$t("print.m1")} ${format(new Date(), "dd.MM.yyyy.")}
  `;

      this.html = printTable({
        title: this.$t("print.maintenance"),
        columns,
        data,
        headerImg,
        footer,
      });
      this.openReportPreview(this.html)
    },
    askConfirmStatusChange(item, newStatusId) {
      if (item.StatusId === newStatusId) return

      this.pendingStatusChange = {
        maintenance: item,
        oldStatusId: item.StatusId,
        newStatusId
      }

      this.confirmStatusDialog = true
    },


    async confirmStatusChange() {
      const { maintenance, newStatusId, oldStatusId } = this.pendingStatusChange
      if (!maintenance) return

      maintenance.statusId = newStatusId

      try {
        await this.$axios.put('Maintenance/EditStatus', {
          AssetId: maintenance.Id,
          StatusId: Number(newStatusId),
          CompanyId: Number(localStorage.getItem('CompanyId'))
        })

        await this.fetchMaintenances()

        this.$store.dispatch('NOTIF_SUCCESS', {
          poruka: this.$t('User_message.m62'),
          position: "top-end",
          timer: 2500,
          action: "noreload"
        })

      } catch (err) {
        maintenance.statusId = oldStatusId
      } finally {
        this.confirmStatusDialog = false
        this.pendingStatusChange = {
          maintenance: null,
          newStatusId: null,
          oldStatusId: null
        }
      }
    },
    cancelStatusChange() {
      const { maintenance, oldStatusId } = this.pendingStatusChange

      if (maintenance) {
        maintenance.statusId = oldStatusId
      }

      this.confirmStatusDialog = false

      this.pendingStatusChange = {
        maintenance: null,
        newStatusId: null,
        oldStatusId: null
      }
    },

    getStatusName(id) {
      return this.statusOptions.find(s => s.id === id)?.name || ''
    },

    openDeleteDialog(item) {
      const row = item.raw ?? item

      this.deleteMaintenanceId = row.Id
      this.deleteMaintenanceName = row.Name
      this.deleteDialog = true
    },

    async confirmDeleteMaintenance() {
      if (!this.deleteMaintenanceId) return

      try {
        await this.$axios.put(
          'Maintenance/Delete/' + this.deleteMaintenanceId
        )

        this.$store.dispatch('NOTIF_SUCCESS', {
          poruka: this.$t('User_message.m60'),
          position: "top-end",
          timer: 2500,
          action: "noreload"
        })

        await this.fetchMaintenances()

      } catch (err) {
        console.error(err)

        this.$store.dispatch('NOTIF_WARNING', {
          poruka: this.$t('User_message.m61'),
          position: 'center',
          icon: 'error',
          timer: 4500
        })

      } finally {
        this.deleteDialog = false
        this.deleteMaintenanceId = null
        this.deleteMaintenanceName = ''
      }
    },

    async fetchMaintenances() {
      this.loading = true;
      try {
        var data = {
          Id: Number(localStorage.getItem('CompanyId')),
          LanguageId: Number(localStorage.getItem('LanguageId'))
        }

        const r = await this.$axios.post('Maintenance/', data)

        this.maintenances = r.data;

      } catch (err) {
        console.error(this.$t('general.noData'), err)
        this.maintenances = []
      } finally {
        this.loading = false
      }
    },
    downloadFile(url) {
      this.$axios.get('Upload?fileName=' + url, { responseType: 'arraybuffer' })
        .then((response) => {
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.setAttribute('download', url)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
    },

    deviceLabel(device) {
      return device ? `${device.type}, model: ${device.model}, inv. broj: ${device.inv_number}` : '';
    },
    getDeviceName(id) {
      const d = this.devices.find((x) => x.id === id);
      return d ? `${d.type}, ${d.model}, inv: ${d.inv_number}` : (id ? `ID: ${id}` : '-');
    },
    getSoftwareName(id) {
      const s = this.softwares.find((x) => x.id === id);
      return s ? s.name : (id ? `ID: ${id}` : '-');
    },
    getFileName(url) {
      return url ? decodeURIComponent(url.split('/').pop()) : '';
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      try {
        return format(new Date(dateStr), 'dd.MM.yyyy');
      } catch {
        return dateStr;
      }
    },

    async saveMaintenance() {
      const now = new Date();
      const timePart = now.toTimeString().split(' ')[0]

      const { valid } = await this.$refs.maintenanceForm.validate() // validacija forme
      if (!valid) return

      if (this.form.document) {
        await this.takeName_files();
      }

      const payload = {
        Name: this.form.contract_name,
        Date: this.form.date_signing_contract
          ? new Date(`${this.form.date_signing_contract}T${timePart}Z`).toISOString()
          : null,

        Expired: this.form.date_expire_contract
          ? new Date(`${this.form.date_expire_contract}T${timePart}Z`).toISOString()
          : null,

        Note: this.form.notes,
        Value: this.parseMoney(this.form.contract_value),
        StatusId: Number(this.form.statusId),
        FileName: this.form.file_name || null,
        CompanyId: Number(localStorage.getItem('CompanyId'))
      }

      try {
        if (this.editMode) {
          // ===== EDIT =====
          await this.$axios.put(
            'Maintenance/Edit',
            {
              ...payload,
              Id: this.selectedId
            }
          )

          this.$store.dispatch('NOTIF_SUCCESS', {
            poruka: this.$t('User_message.m58'),
            position: 'top-end',
            timer: 2500,
            action: 'noreload'
          })

        } else {
          // ===== ADD =====
          await this.$axios.post(
            'Maintenance/Add',
            payload
          )

          if (this.form.document) {
            await this.upload_files()
          }

          this.$store.dispatch('NOTIF_SUCCESS', {
            poruka: this.$t('User_message.m56'),
            position: "top-end",
            timer: 2500,
            action: "noreload",
          })
        }

        this.dialog = false
        await this.fetchMaintenances()

      } catch (err) {
        console.error(err)

        this.$store.dispatch('NOTIF_WARNING', {
          poruka: this.$t('User_message.m59'),
          position: 'center',
          icon: 'error',
          timer: 4500
        })
      }


    },
    async takeName_files() {
      var mjesec, dan, godina, sat, minut, sekunda;
      var now = new Date();

      (now.getUTCMonth() + 1) < 10 ? mjesec = "0" + (now.getUTCMonth() + 1) : mjesec = (now.getUTCMonth() + 1);
      now.getUTCDate() < 10 ? dan = "0" + now.getUTCDate() : dan = now.getUTCDate();
      godina = now.getUTCFullYear().toString().slice(-2);

      now.getHours() < 10 ? sat = "0" + now.getHours() : sat = now.getHours();
      now.getMinutes() < 10 ? minut = "0" + now.getMinutes() : minut = now.getMinutes();
      now.getSeconds() < 10 ? sekunda = "0" + now.getSeconds() : sekunda = now.getSeconds();

      this.form.file_name = "Ugovor_" + localStorage.getItem('CountryId') + "_" + localStorage.getItem('CompanyId') + "_" + mjesec + dan + godina + sat + minut + sekunda + this.takeExtension();
      console.log(this.form.file_name)
      console.log(this.takeExtension())
    },
    takeExtension() {
      var extension = '';

      if (this.form.document.type === 'application/pdf') {
        extension = '.pdf';
      }
      else if (this.form.document.type.startsWith('image/')) {
        extension = '.' + this.form.document.type.split('/')[1];
      }
      return extension;
    },
    async upload_files() {

      //console.log(this.takeExtension());

      const file = new File([this.form.document], this.form.file_name, { type: this.form.document.type })

      this.formData = new FormData();
      this.formData.append("file", file);

      await this.$axios.post('Upload/', this.formData)

    },
    addMaintenance() {
      this.editMode = false;
      this.selectedId = null;
      this.oldDocUrl = null;
      this.resetForm();
      this.dialog = true;

      this.$nextTick(() => {
        this.$refs.maintenanceForm?.resetValidation()
      })
    },
    editMaintenance(item) {
      const row = item.raw ?? item

      this.editMode = true
      this.selectedId = row.Id

      this.form = {
        contract_name: row.Name,
        date_signing_contract: row.Date,
        date_expire_contract: row.Expired,
        notes: row.Note,
        document: null,
        file_name: row.FileName ?? '',
        contract_value: this.formatMoney(row.Value),
        statusId: row.StatusId ?? 1
      }

      this.oldDocUrl = row.FileName
      this.dialog = true

      this.$nextTick(() => {
        this.$refs.maintenanceForm?.resetValidation()
      })
    },
    closeDialog() {
      this.dialog = false;
      this.resetForm();

      this.$nextTick(() => {
        this.$refs.maintenanceForm?.resetValidation()
      })
    },
    resetForm() {
      this.form = {
        contract_name: '',
        date_signing_contract: '',
        date_expire_contract: '',
        notes: '',
        document: null,
        file_name: '',
        contract_value: '',
        statusId: 1,
      };
    },
    changeDoc(value) {
      this.changeDocumentDialog = true;
      //console.log(value)
      this.tempId = value.Id;
    },
    async confirmChangeDocument() {
      if (this.form.document) {
        await this.takeName_files();

        const payload = {
          Id: this.tempId,
          FileName: this.form.file_name || null,
          CompanyId: Number(localStorage.getItem('CompanyId'))
        }

        try {
          await this.$axios.put(
            'Maintenance/EditUgovor',
            payload
          )

          this.$store.dispatch('NOTIF_SUCCESS', {
            poruka: this.$t('User_message.m138'),
            position: "top-end",
            timer: 2500,
            action: "noreload",
          })

          await this.upload_files();

          this.cancelChangeDocument();
          await this.fetchMaintenances();

        } catch (err) {
          console.error(err)

          this.$store.dispatch('NOTIF_WARNING', {
            poruka: this.$t('User_message.m139'),
            position: 'center',
            icon: 'error',
            timer: 4500
          })
        }
      }

    },
    cancelChangeDocument() {
      this.changeDocumentDialog = false;
      this.form.document = null;
    },

    openDeleteDoc(value) {
      this.tempUgovor = value;
      this.deleteDocDialog = true
    },
    async confirmDeleteDoc() {
      if (!this.tempUgovor) return

      try {
        await this.$axios.put('Maintenance/DeleteUgovor', {
          Id: this.tempUgovor.Id,
          CompanyId: Number(localStorage.getItem('CompanyId'))
        })

        this.$store.dispatch('NOTIF_SUCCESS', {
          poruka: this.$t('User_message.m141'),
          position: 'top-end',
          timer: 2500,
          action: 'noreload'
        })

        this.deleteDocDialog = false
        await this.fetchMaintenances()
        this.tempUgovor = null

      } catch (err) {
        console.error(err)
        this.$store.dispatch('NOTIF_WARNING', {
          poruka: this.$t('User_message.m142'),
          position: 'center',
          icon: 'error',
          timer: 4500
        })
      }
    }
  },
};
</script>
<style scoped>
.remaining-chip {
  display: inline-flex;
  justify-content: flex-end;
}

.chip-number {
  width: 100%;
  text-align: right;
}
</style>